/**
 * Дашборд приходит одним запросом: `GET /admin/dashboard` считает все панели
 * сам, включая сравнение с прошлым периодом такой же длины
 * (03_dorixona_monorepo/backend/core/api/openapi.yaml › Admin · reports).
 *
 * Здесь только перевод ответа в имена экрана; ни одной цифры мы не пересчитываем
 * — иначе админка и отчёты разошлись бы в мелочах округления.
 */
import { useQuery } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'

type TotalsDto = components['schemas']['DashboardTotals']
type PointDto = components['schemas']['Point']

interface DashboardDto extends TotalsDto {
  from?: string
  to?: string
  compare?: TotalsDto
  revenue_by_day?: PointDto[]
  top_products?: PointDto[]
  top_categories?: PointDto[]
  top_pharmacies_pickup?: PointDto[]
  top_pharmacies_delivery?: PointDto[]
  delivery_by_district?: PointDto[]
  attention?: {
    no_photo?: number
    no_category?: number
    out_of_stock?: number
    needs_decision?: number
  }
}

export interface Totals {
  revenueOnline: number
  revenueOffline: number
  revenueTotal: number
  ordersPickup: number
  ordersDelivery: number
  ordersInWork: number
  averageOrder: number
  pointsSpent: number
  cashbackGiven: number
  newCustomers: number
}

export interface Point {
  label: string
  value: number
  count: number
}

export interface Attention {
  noPhoto: number
  noCategory: number
  outOfStock: number
  needsDecision: number
}

export interface Dashboard {
  totals: Totals
  compare: Totals
  revenueByDay: Point[]
  topProducts: Point[]
  topCategories: Point[]
  topPharmaciesPickup: Point[]
  topPharmaciesDelivery: Point[]
  deliveryByDistrict: Point[]
  attention: Attention
}

export interface DashboardParams {
  /** Границы периода как `YYYY-MM-DD`: сервер считает их в ташкентском времени. */
  from: string
  to: string
  /** 0 — вся сеть; иначе одна аптека. Сотруднику аптеки сервер сузит сам. */
  storeId: number
}

const ZERO_TOTALS: Totals = {
  revenueOnline: 0,
  revenueOffline: 0,
  revenueTotal: 0,
  ordersPickup: 0,
  ordersDelivery: 0,
  ordersInWork: 0,
  averageOrder: 0,
  pointsSpent: 0,
  cashbackGiven: 0,
  newCustomers: 0,
}

function toTotals(dto: TotalsDto | undefined): Totals {
  if (!dto) return ZERO_TOTALS
  return {
    revenueOnline: dto.revenue_online ?? 0,
    revenueOffline: dto.revenue_offline ?? 0,
    revenueTotal: dto.revenue_total ?? 0,
    ordersPickup: dto.orders_pickup ?? 0,
    ordersDelivery: dto.orders_delivery ?? 0,
    ordersInWork: dto.orders_in_work ?? 0,
    averageOrder: dto.average_order ?? 0,
    pointsSpent: dto.points_spent ?? 0,
    cashbackGiven: dto.cashback_given ?? 0,
    newCustomers: dto.new_customers ?? 0,
  }
}

function toPoints(dto: PointDto[] | undefined): Point[] {
  return (dto ?? []).map((p) => ({
    label: p.label ?? '',
    value: p.value ?? 0,
    count: p.count ?? 0,
  }))
}

export function toDashboard(dto: DashboardDto): Dashboard {
  return {
    totals: toTotals(dto),
    compare: toTotals(dto.compare),
    revenueByDay: toPoints(dto.revenue_by_day),
    topProducts: toPoints(dto.top_products),
    topCategories: toPoints(dto.top_categories),
    topPharmaciesPickup: toPoints(dto.top_pharmacies_pickup),
    topPharmaciesDelivery: toPoints(dto.top_pharmacies_delivery),
    deliveryByDistrict: toPoints(dto.delivery_by_district),
    attention: {
      noPhoto: dto.attention?.no_photo ?? 0,
      noCategory: dto.attention?.no_category ?? 0,
      outOfStock: dto.attention?.out_of_stock ?? 0,
      needsDecision: dto.attention?.needs_decision ?? 0,
    },
  }
}

export async function fetchDashboard(
  api: ApiClient,
  { from, to, storeId }: DashboardParams,
): Promise<Dashboard> {
  const dto = await api.get<DashboardDto>('/admin/dashboard', {
    query: { from, to, store_id: storeId || undefined },
  })
  return toDashboard(dto)
}

/**
 * Итоги дашборда за период — и для всей сети, и для одной аптеки (карточка
 * аптеки берёт отсюда выручку и средний чек: сервер считает их по всем
 * заказам, а не по первой странице списка).
 */
export function useDashboard(params: DashboardParams, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: keys.dashboard(params.from, params.to, params.storeId),
    queryFn: () => fetchDashboard(api, params),
    enabled,
  })
}
